import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  Timestamp,
  deleteDoc
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

Modal.setAppElement('#root');

const Vendor = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expenseAccounts, setExpenseAccounts] = useState([]);

  const [newVendor, setNewVendor] = useState({
    name: '',
    contactEmail: '',
    contactPhone: '',
    paymentTerms: 'Net 30',
    defaultExpenseAccountId: '',
    userId: user?.uid || ''
  });
  const [editingVendor, setEditingVendor] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [currentVendor, setCurrentVendor] = useState(null);
  const [isFetchingVendors, setIsFetchingVendors] = useState(true);
  const [hasFetchedVendors, setHasFetchedVendors] = useState(false);
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('excel');
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');

  const vendorsCollectionRef = collection(db, "vendors");
  const accountsCollectionRef = collection(db, "accounts");

  useEffect(() => {
    if (user && userData) {
      fetchExpenseAccounts();
      fetchVendors();
    }
  }, [user, userData]);

  useEffect(() => {
    const filtered = vendors.filter((vendor) =>
      vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.contactEmail.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredVendors(filtered);
  }, [searchTerm, vendors]);

  const fetchExpenseAccounts = async () => {
    if (!user?.uid) return;
    try {
      const q = query(accountsCollectionRef, where("type", "==", "Expense"));
      const data = await getDocs(q);
      const accountList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setExpenseAccounts(accountList);
    } catch (error) {
      console.error("Error fetching expense accounts:", error);
      setError("Failed to fetch expense accounts");
      setShowModal(true);
    }
  };

  const fetchVendors = async () => {
    if (!user?.uid) return;
    
    setIsFetchingVendors(true);
    try {
      const q = query(vendorsCollectionRef);
      const data = await getDocs(q);
      const vendorList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setVendors(vendorList);
      setHasFetchedVendors(true);
    } catch (error) {
      console.error("Error fetching vendors:", error);
      setError("Failed to fetch vendors");
      setShowModal(true);
    } finally {
      setIsFetchingVendors(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewVendor({ ...newVendor, [name]: value });
  };

  const handleAddVendor = async () => {
    if (!newVendor.name.trim()) {
      setError("Vendor name cannot be empty");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      await addDoc(vendorsCollectionRef, {
        ...newVendor,
        userId: user?.uid,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Vendor added successfully");
      fetchVendors();
      resetNewVendor();
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add vendor");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateVendor = async () => {
    setUpdate(true);
    try {
      const vendorDoc = doc(db, "vendors", editingVendor.id);
      await updateDoc(vendorDoc, newVendor);
      setSuccessMessage("Vendor updated successfully");
      fetchVendors();
      resetNewVendor();
      setEditingVendor(null);
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update vendor");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const resetNewVendor = () => {
    setNewVendor({
      name: '',
      contactEmail: '',
      contactPhone: '',
      paymentTerms: 'Net 30',
      defaultExpenseAccountId: '',
      userId: user?.uid || ''
    });
  };

  const handleEditVendor = (vendorToEdit) => {
    setEditingVendor(vendorToEdit);
    setNewVendor({
      name: vendorToEdit.name,
      contactEmail: vendorToEdit.contactEmail || '',
      contactPhone: vendorToEdit.contactPhone || '',
      paymentTerms: vendorToEdit.paymentTerms,
      defaultExpenseAccountId: vendorToEdit.defaultExpenseAccountId || '',
      userId: vendorToEdit.userId
    });
    setIsEditModalOpen(true);
  };

  const confirmDeleteVendor = (vendorToDelete) => {
    setCurrentVendor(vendorToDelete);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteVendor = async (id) => {
    setUpdate(true);
    try {
      const vendorDoc = doc(db, "vendors", id);
      await deleteDoc(vendorDoc);
      setSuccessMessage("Vendor deleted successfully");
      fetchVendors();
    } catch (error) {
      setError("Failed to delete vendor");
    } finally {
      setUpdate(false);
      setIsDeleteModalOpen(false);
      setShowModal(true);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleFileTypeChange = (e) => {
    setFileType(e.target.value);
    setFile(null);
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return !email || re.test(email);
  };

  const handleBulkImport = async () => {
    if (!file) {
      setError("Please select a file first");
      setShowModal(true);
      return;
    }

    setUpdate(true);
    setImportStatus('Processing file...');
    setImportProgress(0);

    try {
      let vendorsData = [];

      if (fileType === 'excel') {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        vendorsData = XLSX.utils.sheet_to_json(worksheet);
      } else {
        const text = await file.text();
        const result = Papa.parse(text, {
          header: true,
          skipEmptyLines: true
        });
        vendorsData = result.data;
      }

      if (vendorsData.length === 0) {
        throw new Error("No vendor data found in the file");
      }

      setImportStatus(`Importing ${vendorsData.length} vendors...`);

      let successCount = 0;
      let errorCount = 0;
      const validPaymentTerms = ['Net 30', 'Net 15', 'Due on Receipt', 'Custom'];

      for (let i = 0; i < vendorsData.length; i++) {
        try {
          const vendor = vendorsData[i];

          if (!vendor.name) {
            errorCount++;
            continue;
          }

          const contactEmail = vendor.contactEmail?.trim() || '';
          if (contactEmail && !validateEmail(contactEmail)) {
            errorCount++;
            continue;
          }

          const paymentTerms = vendor.paymentTerms?.trim() || 'Net 30';
          if (!validPaymentTerms.includes(paymentTerms)) {
            errorCount++;
            continue;
          }

          const defaultExpenseAccountName = vendor.defaultExpenseAccountName?.trim() || '';
          const account = expenseAccounts.find(acc => acc.name.toLowerCase() === defaultExpenseAccountName.toLowerCase());
          const defaultExpenseAccountId = account ? account.id : '';

          const vendorData = {
            name: vendor.name.trim(),
            contactEmail,
            contactPhone: vendor.contactPhone?.trim() || '',
            paymentTerms,
            defaultExpenseAccountId,
            userId: user?.uid,
            createdAt: Timestamp.now()
          };

          await addDoc(vendorsCollectionRef, vendorData);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Error importing vendor ${i + 1}:`, error);
        }

        setImportProgress(Math.round(((i + 1) / vendorsData.length) * 100));
      }

      setImportStatus(`Import complete: ${successCount} successful, ${errorCount} failed`);
      setSuccessMessage(`Bulk import completed: ${successCount} vendors added successfully`);
      fetchVendors();
    } catch (error) {
      setError(`Bulk import failed: ${error.message}`);
      setShowModal(true);
    } finally {
      setUpdate(false);
      setFile(null);
      setTimeout(() => {
        setIsBulkImportModalOpen(false);
        setImportProgress(0);
        setImportStatus('');
      }, 2000);
    }
  };

  if (loading) return <Loader text={"Loading"} />;
  if (!userData) return <Loader text={"Reading"} />;

  const allowedRoles = ['Cashier', 'Accountant', 'Admin', 'Supreme'];

  const displayContent = () => {
    if (isFetchingVendors && !hasFetchedVendors) {
      return <p className="loading-text">Loading vendors...</p>;
    }
    
    if (filteredVendors.length === 0) {
      if (searchTerm) {
        return <p className="loading-text">No vendors match your search.</p>;
      } else {
        return <p className="loading-text">No vendors found. Add your first vendor.</p>;
      }
    }
    
    return filteredVendors.map((vendorItem) => (
      <div key={vendorItem.id} className="class-item">
        <div className="column gap-1">
          <p><strong>{vendorItem.name}</strong><br />
            <span>Email: {vendorItem.contactEmail}</span><br />
            <span>Phone: {vendorItem.contactPhone}</span><br />
            <span>Terms: {vendorItem.paymentTerms}</span><br />
            <span>Default Account: {expenseAccounts.find(a => a.id === vendorItem.defaultExpenseAccountId)?.name || 'None'}</span>
          </p>                        
        </div>
        <div className="row align-center justify-center gap-15">
          <button className="list-btn" onClick={() => handleEditVendor(vendorItem)}>
            <i className="fas fa-edit"></i>
          </button>
          <button className="list-btn delete-btn" onClick={() => confirmDeleteVendor(vendorItem)}>
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>
    ));
  };

  return (
    <>
      {update && <Loader text={"Processing"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Vendors</h2>
            <Link to="#" className="home-link" onClick={() => fetchVendors()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">
            <div className="row justify-center align-center gap-15 add-buttons margin-top-cashflow1">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Vendor</button>
              <button className="login-button login-button-d caps" onClick={() => setIsBulkImportModalOpen(true)}>Bulk Import</button>
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {displayContent()}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Vendor"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Vendor</h2>
        <form className="session-form" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <label htmlFor="name" className="input-label">Name</label>
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Vendor Name"
            value={newVendor.name}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="contactEmail" className="input-label">Email</label>
          <input
            type="email"
            name="contactEmail"
            className="input-field"
            placeholder="Contact Email"
            value={newVendor.contactEmail}
            onChange={handleInputChange}
          />
          <label htmlFor="contactPhone" className="input-label">Phone</label>
          <input
            type="text"
            name="contactPhone"
            className="input-field"
            placeholder="Contact Phone"
            value={newVendor.contactPhone}
            onChange={handleInputChange}
          />
          <label htmlFor="paymentTerms" className="input-label">Payment Terms</label>
          <select
            name="paymentTerms"
            className="input-field"
            value={newVendor.paymentTerms}
            onChange={handleInputChange}
          >
            <option value="Net 30">Net 30</option>
            <option value="Net 15">Net 15</option>
            <option value="Due on Receipt">Due on Receipt</option>
            <option value="Custom">Custom</option>
          </select>
          <label htmlFor="defaultExpenseAccountId" className="input-label">Default Expense Account</label>
          <select
            name="defaultExpenseAccountId"
            className="input-field"
            value={newVendor.defaultExpenseAccountId}
            onChange={handleInputChange}
          >
            <option value="">Select Default Expense Account (Optional)</option>
            {expenseAccounts.map(acc => (
              <option key={acc.id} value={acc.id}>{acc.name}</option>
            ))}
          </select>
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddVendor}>Add Vendor</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          resetNewVendor();
          setEditingVendor(null);
        }}
        contentLabel="Edit Vendor"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Vendor</h2>
        <form className="session-form" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <label htmlFor="name" className="input-label">Name</label>
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Vendor Name"
            value={newVendor.name}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="contactEmail" className="input-label">Email</label>
          <input
            type="email"
            name="contactEmail"
            className="input-field"
            placeholder="Contact Email"
            value={newVendor.contactEmail}
            onChange={handleInputChange}
          />
          <label htmlFor="contactPhone" className="input-label">Phone</label>
          <input
            type="text"
            name="contactPhone"
            className="input-field"
            placeholder="Contact Phone"
            value={newVendor.contactPhone}
            onChange={handleInputChange}
          />
          <label htmlFor="paymentTerms" className="input-label">Payment Terms</label>
          <select
            name="paymentTerms"
            className="input-field"
            value={newVendor.paymentTerms}
            onChange={handleInputChange}
          >
            <option value="Net 30">Net 30</option>
            <option value="Net 15">Net 15</option>
            <option value="Due on Receipt">Due on Receipt</option>
            <option value="Custom">Custom</option>
          </select>
          <label htmlFor="defaultExpenseAccountId" className="input-label">Default Expense Account</label>
          <select
            name="defaultExpenseAccountId"
            className="input-field"
            value={newVendor.defaultExpenseAccountId}
            onChange={handleInputChange}
          >
            <option value="">Select Default Expense Account (Optional)</option>
            {expenseAccounts.map(acc => (
              <option key={acc.id} value={acc.id}>{acc.name}</option>
            ))}
          </select>
          <div className="row align-center justify-center gap-1">
            <button className="login-button" type="button" onClick={() => {
              setIsEditModalOpen(false);
              resetNewVendor();
              setEditingVendor(null);
            }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateVendor}>Update Vendor</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        contentLabel="Delete Vendor"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Delete Vendor</h2>
        {currentVendor && (
          <div>
            <p>Are you sure you want to delete <strong>{currentVendor.name}</strong>? This action cannot be undone.</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
              <button className="login-button delete-btn" type="button" onClick={() => handleDeleteVendor(currentVendor.id)}>Delete</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isBulkImportModalOpen}
        onRequestClose={() => {
          setIsBulkImportModalOpen(false);
          setFile(null);
          setImportProgress(0);
          setImportStatus('');
        }}
        contentLabel="Bulk Import Vendors"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Bulk Import Vendors</h2>
        <div className="modal-form">
          <div style={{ marginBottom: '5px' }}>
            <label>
              <input
                type="radio"
                name="fileType"
                value="excel"
                checked={fileType === 'excel'}
                onChange={handleFileTypeChange}
              /> Excel File (.xlsx)
            </label>
            <label>
              <input
                type="radio"
                name="fileType"
                value="csv"
                checked={fileType === 'csv'}
                onChange={handleFileTypeChange}
              /> CSV File (.csv)
            </label>
          </div>
          
          <input
            type="file"
            accept={fileType === 'excel' ? '.xlsx, .xls' : '.csv'}
            onChange={handleFileChange}
            className="input-field1"
          />
          
          {importStatus && (
            <div>
              <p>{importStatus}</p>
              {importProgress > 0 && (
                <div style={{ width: '100%', backgroundColor: '#ddd', borderRadius: '5px' }}>
                  <div
                    style={{
                      width: `${importProgress}%`,
                      height: '20px',
                      backgroundColor: '#4CAF50',
                      borderRadius: '5px',
                      textAlign: 'center',
                      lineHeight: '20px',
                      color: 'white'
                    }}
                  >
                    {importProgress}%
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="row align-center justify-center gap-15">
            <button
              type="button"
              className="login-button caps"
              onClick={() => {
                setIsBulkImportModalOpen(false);
                setFile(null);
                setImportProgress(0);
                setImportStatus('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="login-button caps"
              onClick={handleBulkImport}
              disabled={!file || update}
            >
              Import
            </button>
          </div>
          
          <div className="import-instructions">
            <h4>File Format Instructions:</h4>
            <p><strong>REQUIRED FIELDS:</strong> name</p>
            <p><strong>OPTIONAL FIELDS:</strong> contactEmail, contactPhone, paymentTerms, defaultExpenseAccountName</p>
            <p><strong>VALID PAYMENT TERMS:</strong> Net 30, Net 15, Due on Receipt, Custom</p>
            <p><strong>FOR EXCEL:</strong> First row should contain column headers</p>
            <p><strong>FOR CSV:</strong> First row should contain column headers, comma-separated</p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Vendor;